import { useCallback, useEffect, useState, useMemo } from "react";
import { FeatureFlagKey } from "../../../common/enums";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { useFeatureFlag } from "../../../common/infrastructure/hooks/useFeatureFlag";
import { shuffleReviews } from "../utils/reviewUtility";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { IPublicReviewsSummaryModel } from "../models/reviewModels";

export const QUOTE_ROTATION_MS = 6000;

/**
 * The quote under "Добре дошли!": the studio's rating and one review at a time, moving
 * on every few seconds. A click opens the dialog with every review; the rotation waits
 * while it is open. Nothing shows, and nothing is asked, while the reviews flag is off.
 */
export const useReviewQuote = (studioId?: number) => {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isReviewsEnabled = useFeatureFlag(FeatureFlagKey.EnableReviewRequests, false);

  const { data } = useApiQuery<IPublicReviewsSummaryModel | null>({
    queryKey: [endpointsMetadata.publicReviews.getSummary, studioId],
    endpoint: `${endpointsMetadata.publicReviews.getSummary}?studioId=${studioId}`,
    enabled: isReviewsEnabled && !!studioId,
    // A small extra on the home screen; it must never block the page behind the loader.
    globalIsLoading: false,
    retry: false,
  });

  // Below the studio's minimum the API answers 204, which arrives as "" rather than null.
  // The quotes come in a new order on every visit, even when the answer is cached.
  // A cached answer stays hidden when the flag is switched off meanwhile.
  const summary = useMemo(
    () =>
      isReviewsEnabled && data && typeof data === "object"
        ? { ...data, quotes: shuffleReviews(data.quotes ?? []) }
        : null,
    [data, isReviewsEnabled],
  );
  const quotesCount = summary?.quotes?.length ?? 0;

  useEffect(() => {
    setQuoteIndex(0);
  }, [studioId, quotesCount]);

  useEffect(() => {
    if (quotesCount < 2 || isDialogOpen) {
      return;
    }

    const timer = setInterval(
      () => setQuoteIndex((index) => (index + 1) % quotesCount),
      QUOTE_ROTATION_MS,
    );
    return () => clearInterval(timer);
  }, [quotesCount, isDialogOpen]);

  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  return {
    summary,
    currentQuote: quotesCount > 0 ? summary?.quotes[quoteIndex] : undefined,
    quoteIndex,
    isDialogOpen,
    openDialog,
    closeDialog,
  };
};
